import type { RecordLastDoneInput } from '@chukua/common';
import { Alert, DatePicker, Form, InputNumber, Modal, Select } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import type { TruckServiceItemView } from '../truck-service.api';

interface ServiceRecordModalProps {
  open: boolean;
  /** The types of service this truck can record: the items of its plan. */
  serviceTypes: TruckServiceItemView[];
  error: string | null;
  submitting: boolean;
  onSubmit: (item: { id: string; name: string }, input: RecordLastDoneInput) => Promise<boolean>;
  onClose: () => void;
}

interface FormValues {
  serviceItemId: string;
  odometerKm: number | null;
  doneOn: Dayjs | null;
  engineHours: number | null;
}

/** An entry is evidence, never edited: a correction is a new entry and the newest one wins. */
export function ServiceRecordModal({ open, serviceTypes, error, submitting, onSubmit, onClose }: ServiceRecordModalProps) {
  const [form] = Form.useForm<FormValues>();

  async function submit(values: FormValues) {
    const type = serviceTypes.find((candidate) => candidate.serviceItemId === values.serviceItemId);
    if (!type) return;
    if (values.odometerKm == null && values.doneOn == null && values.engineHours == null) {
      form.setFields([{ name: 'odometerKm', errors: ['Give at least one of odometer, date or engine hours'] }]);
      return;
    }
    const input = { odometerKm: values.odometerKm, doneOn: values.doneOn ? values.doneOn.format('YYYY-MM-DD') : null, engineHours: values.engineHours };
    if (await onSubmit({ id: type.serviceItemId, name: type.name }, input)) onClose();
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText="Add entry"
      okButtonProps={{ loading: submitting }}
      title="Add a service entry"
      destroyOnHidden
    >
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<FormValues> form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={submitting}>
        <Form.Item name="serviceItemId" label="Type of service" rules={[{ required: true, message: 'Choose the type of service' }]}>
          <Select
            placeholder="Select or search a type of service"
            showSearch
            optionFilterProp="label"
            options={serviceTypes.map((type) => ({ value: type.serviceItemId, label: type.name }))}
          />
        </Form.Item>
        <Form.Item name="odometerKm" label="Odometer (km)">
          <InputNumber className="w-full" min={0} precision={0} />
        </Form.Item>
        <Form.Item name="doneOn" label="Date done" getValueProps={(value) => ({ value: value ?? null })}>
          <DatePicker className="w-full" disabledDate={(day) => day.isAfter(dayjs(), 'day')} />
        </Form.Item>
        <Form.Item name="engineHours" label="Engine hours" extra="Entered by hand: trucks don't report engine hours yet.">
          <InputNumber className="w-full" min={0} step={0.1} precision={1} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
